"use client";

import React, { useState, useEffect } from "react";
import { Clock, Building, X, Trash2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  getRecentSearches,
  clearRecentSearches,
  removeRecentSearch,
  type RecentSearch
} from "@/lib/recent-searches";
import {
  getRecentListings,
  addRecentListing,
  clearRecentListings,
  removeRecentListing,
  type RecentListing
} from "@/lib/recent-listings";
import Image from "next/image";
import Link from "next/link";

interface RecentSearchesDropdownProps {
  onSelectSearch: (query: string) => void;
  className?: string;
}

/**
 * Recent Searches Dropdown Component with Tabs
 *
 * Displays two tabs:
 * 1. Recent Searches - Recent search queries
 * 2. Recent Listings - Recently viewed listings
 *
 * @author USLand UX Team
 */
export function RecentSearchesDropdown({
  onSelectSearch,
  className
}: RecentSearchesDropdownProps) {
  const [searches, setSearches] = useState<RecentSearch[]>([]);
  const [listings, setListings] = useState<RecentListing[]>([]);

  // Load data on mount
  useEffect(() => {
    setSearches(getRecentSearches());
    setListings(getRecentListings());
  }, []);

  const handleSelectSearch = (query: string) => {
    onSelectSearch(query);
  };

  const handleRemoveSearch = (query: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeRecentSearch(query);
    setSearches(getRecentSearches());
  };

  const handleClearAllSearches = () => {
    clearRecentSearches();
    setSearches([]);
  };

  const handleRemoveListing = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeRecentListing(id);
    setListings(getRecentListings());
  };

  const handleClearAllListings = () => {
    clearRecentListings();
    setListings([]);
  };

  const hasContent = searches.length > 0 || listings.length > 0;

  if (!hasContent) {
    return (
      <div className={cn("p-4 text-sm text-muted-foreground text-center", className)}>
        No recent activity yet
      </div>
    );
  }

  return (
    <div className={cn("min-w-[320px]", className)}>
      <Tabs defaultValue="searches" className="w-full">
        <TabsList className="w-full grid grid-cols-2 rounded-lg bg-muted p-1 text-muted-foreground">
          <TabsTrigger value="searches" className="text-sm py-1.5 rounded-md font-medium transition-all data ">
            <Search className="h-3.5 w-3.5" />
            <span>Searches</span>
            {searches.length > 0 && (
              <span className="ml-auto text-xs bg- px-1.5 rounded-full">
                {searches.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="listings" className="text-sm gap-1.5">
            <Building className="h-3.5 w-3.5" />
            <span>Listings</span>
            {listings.length > 0 && (
              <span className="ml-auto text-xs px-1.5 rounded-full">
                {listings.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Recent Searches Tab */}
        <TabsContent value="searches" className="mt-0">
          {searches.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              No recent searches
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b">
                <div className="flex items-center gap-2 text-sm text-muted-foreground font-medium">
                  <Clock className="h-4 w-4" />
                  <span>Recent Searches</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAllSearches}
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              </div>

              {/* Search List */}
              <div className="max-h-[300px] overflow-y-auto">
                {searches.map((search, index) => (
                  <div
                    key={`${search.query}-${index}`}
                    className="search-dropdown-item-wrapper"
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectSearch(search.query)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelectSearch(search.query);
                        }
                      }}
                      className={cn(
                        "w-full flex items-center justify-between gap-2 px-4 py-2.5",
                        "text-left text-sm transition-colors cursor-pointer",
                        "group"
                      )}
                  >
                    <div className="flex-1 truncate">
                      <span className="text-foreground">{search.query}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {formatTimestamp(search.timestamp)}
                      </span>
                    </div>
                    <button
                      onClick={(e) => handleRemoveSearch(search.query, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-background rounded transition-opacity"
                      aria-label="Remove search"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Recent Listings Tab */}
        <TabsContent value="listings" className="mt-0">
          {listings.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              No recent listings
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Building className="h-4 w-4" />
                  <span>Recent Listings</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAllListings}
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              </div>

              {/* Listings List */}
              <div className="max-h-[300px] overflow-y-auto">
                {listings.map((listing) => (
                  <Link
                    key={listing.id}
                    href={`/listing/${listing.id}`}
                    className="block"
                  >
                    <div className="search-dropdown-item-wrapper">
                      <div className="flex items-center gap-3 px-4 py-3 transition-colors group">
                      {/* Thumbnail */}
                      <div className="relative h-12 w-12 rounded-md overflow-hidden bg-muted flex-shrink-0">
                        {listing.image ? (
                          <Image
                            src={listing.image}
                            alt={listing.title}
                            fill
                            className="object-cover"
                          />
                        ) : (
                          <div className="h-full w-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                            <Building className="h-5 w-5 text-white opacity-80" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                          {listing.title}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {listing.city && listing.state && (
                            <span>{listing.city}, {listing.state}</span>
                          )}
                          <span>•</span>
                          <span>${(listing.price / 1000000).toFixed(2)}M</span>
                          <span>•</span>
                          <span>{formatTimestamp(listing.timestamp)}</span>
                        </div>
                      </div>

                      {/* Remove button */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveListing(listing.id, e);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-background rounded transition-opacity flex-shrink-0"
                        aria-label="Remove listing"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Format timestamp to relative time
 */
function formatTimestamp(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString();
}
